<template lang="pug">
.establishment-editor
    v-page-loader(:show="isLoading")
    v-card.elevation-10(v-if="!isLoading" )
        v-card-title.editor-title.mb-3
            h2.headline {{ $vuetify.lang.t(`$vuetify.pages.EstablishmentEditor.${isEdit ? 'edit' : 'create'}`)}}
            .d-flex
              v-checkbox.establishment-alarm-activity(
                v-model="establishment.closedOnAlarm"
                :label="$vuetify.lang.t(`$vuetify.pages.Establishments.closedOnAlarm`)"
                color="secondary"
              )
              v-checkbox.establishment-status(
                  v-model="establishment.enabled"
                  :label="$vuetify.lang.t(`$vuetify.pages.Establishments.${establishment.enabled ? 'active' : 'inactive'}`)"
                  color="secondary"
              )
        v-card-text
            v-locale(controlled v-model="editingLocale")
            v-row.mt-3
                v-col.py-0(cols="12" sm="4")
                    v-text-field(
                        v-model="establishment[localizeField('name', editingLocale)]"
                        :label="$vuetify.lang.t('$vuetify.pages.EstablishmentEditor.name') + ' *'"
                        counter="255"
                        maxLength="255"
                        outlined
                    )
                v-col.py-0(cols="12" sm="4")
                    v-select(
                        :label="$vuetify.lang.t('$vuetify.pages.EstablishmentEditor.type')"
                        :items="types"
                        :item-text="'name:' + $vuetify.lang.current"
                        item-value="id"
                        v-model="establishment.typeIds"
                        outlined
                        clearable
                        multiple
                    )
                v-col.py-0(cols="12" sm="4")
                    v-select(
                        :label="$vuetify.lang.t('$vuetify.pages.EstablishmentEditor.category') + ' *'"
                        :items="categories",
                        :error-messages="getValidateMessage('Establishment', 'establishment.categoryId', 'category')"
                        :item-text="localizeField('name', $vuetify.lang.current)"
                        item-value="id"
                        v-model="establishment.categoryId"
                        outlined
                        clearable
                    )
            v-row
                v-col.py-0(cols="12" sm="3")
                    v-select(
                        :disabled="!!userBrandId"
                        v-model="establishment.brandId"
                        :label="$vuetify.lang.t('$vuetify.pages.EstablishmentEditor.brand') + ' *'"
                        :error-messages="getValidateMessage('Establishment', 'establishment.brandId', 'brand')"
                        :items="brands"
                        item-value="id"
                        item-text="name"
                        outlined
                    )
                        template(v-slot:selection="{ item }")
                            span {{ item.id | brand }}
                        template(v-slot:item="{ item }")
                            span {{ item.id | brand }}
                v-col.py-0(cols="12" sm="9")
                    v-row.ma-0
                        v-col.py-0(cols="12" sm="3")
                            v-text-field(
                                v-model="establishment.minimalPrice"
                                :label="$vuetify.lang.t('$vuetify.pages.EstablishmentEditor.minimalPrice') + ' *'"
                                :error-messages="getValidateMessage('Establishment', 'establishment.minimalPrice', 'minimalPrice')"
                                outlined
                                type="number"
                            )
                        v-col.py-0(cols="12" sm="3")
                            v-text-field(
                                v-model="establishment.cookTime"
                                :label="$vuetify.lang.t('$vuetify.pages.EstablishmentEditor.cookTime') + ' *'"
                                :error-messages="getValidateMessage('Establishment', 'establishment.cookTime', 'cookTime')"
                                outlined
                                type="number"
                            )
                        v-col.py-0(cols="12" sm="3")
                            v-text-field(
                              v-model="establishment.commission"
                              :label="$vuetify.lang.t('$vuetify.pages.EstablishmentEditor.commission') + ' *'"
                              :error-messages="getValidateMessage('Establishment', 'establishment.commission', 'commission')"
                              outlined
                              type="number"
                              append-icon="percent"
                            )
                        v-col.py-0(cols="12" sm="3")
                            v-text-field(
                              v-model="establishment.margin"
                              :label="$vuetify.lang.t('$vuetify.pages.Establishments.table.margin') + ' *'"
                              :error-messages="getValidateMessage('Establishment', 'establishment.margin', 'margin')"
                              outlined
                              type="number"
                              append-icon="percent"
                            )
            v-row.mb-3
                v-col.py-0(cols="12" sm="6")
                    v-text-field(
                        v-model="establishment.managerPhoneNumber"
                        :label="$vuetify.lang.t('$vuetify.pages.EstablishmentEditor.managerPhoneNumber')"
                        rows="2"
                        auto-grow
                        outlined
                    )
                v-col.py-0(cols="12" sm="6")
                    v-text-field(
                        v-model="establishment[localizeField('description', editingLocale)]"
                        :label="$vuetify.lang.t('$vuetify.pages.EstablishmentEditor.description')"
                        :counter="255"
                        maxLength="255"
                        rows="2"
                        auto-grow
                        outlined
                    )
            v-row.mb-3
                v-col.py-0.mb-3(cols="12" sm="6")
                    v-autocomplete.address-input(
                        @change="onAddressSelect"
                        :search-input.sync="addressSearch"
                        :label="$vuetify.lang.t('$vuetify.pages.EstablishmentEditor.place') + (establishment.addresses.length ? ` (${establishment.addresses.length})` : ' *')"
                        :placeholder="$vuetify.lang.t('$vuetify.pages.ReceiveOrders.search')"
                        :items="autocompleteAddresses"
                        :menu-props="{ maxHeight: 300 }"
                        item-value="id"
                        item-text="description"
                        autocomplete="off"
                        persistent-hint
                        disable-lookup
                        return-object
                        hide-no-data
                        no-filter
                        clearable
                        outlined
                        hide-details
                    )
                        template(v-if="establishment.addresses.length" v-slot:append-outer)
                            v-menu(
                                :close-on-content-click="false"
                                open-delay="200"
                                open-on-click
                                offset-x
                                nudge-right="5"
                            )
                                template(v-slot:activator="{ on, attrs }")
                                    v-btn(v-on="on" v-bind="attrs" :title="`Список адресов (${establishment.addresses.length})`" icon)
                                        v-icon mdi-format-list-bulleted
                                .addresses-wrap
                                    .address(v-for="(item, index) in establishment.addresses" :key="item.id")
                                        .address__icon
                                            v-checkbox(v-model="item.status === 'active'" @change="toggleAvailability({addressId: item.id, currentStatus: item.status})" color="dark darken-1" hide-details class="ma-0" :disabled="!item.id")
                                        .address__description
                                            span {{ item.description }}
                                        .address__actions.d-flex
                                            v-btn(@click="openScheduleModal(item)" icon)
                                              v-icon mdi-calendar-clock
                                            v-btn.remove(@click="onRemoveAddressClick(item)" icon)
                                                v-icon clear
                v-col.py-0.mb-3(cols="12" sm="6")
                    v-row.pl-7
                        v-col(cols="12" sm="6")
                            v-text-field(
                              v-model="establishment.openTime"
                              :label="$vuetify.lang.t('$vuetify.pages.EstablishmentEditor.openTime')"
                              type="time"
                              outlined
                            )
                        v-col(cols="12" sm="6")
                            v-text-field(
                              v-model="establishment.closeTime"
                              :label="$vuetify.lang.t('$vuetify.pages.EstablishmentEditor.closeTime')"
                              type="time"
                              outlined
                            )
            v-image-uploader(
                :width="280"
                :height="210"
                :initial-image="establishment.imgUrl"
                :input-label="$vuetify.lang.t('$vuetify.pages.Delivery.photo')"
                :error-message="getValidateMessage('Establishment', 'establishment.image', 'image')"
                @choose-image="onChooseImage"
            )
            .actions.d-flex.justify-end
                v-btn(
                    :loading="requestProcessing"
                    v-if="canSaveChanges"
                    :disabled="!isCustomValid"
                    @click="saveEstablishment"
                    dark
                ) {{ $vuetify.lang.t(`$vuetify.buttons.${isEdit ? 'save' : 'create'}`) }}
    v-btn.mx-2.arrow-back(to="/establishments" fab small dark)
        v-icon(dark) arrow_back
    v-dialog(
      v-model="scheduleModal"
      persistent
      width="550px"
    )
      v-card
        v-card-title
          span.title.mx-auto {{$vuetify.lang.t('$vuetify.pages.Delivery.addressSchedule')}}
        v-card-text
          .text-center
            span {{scheduleModalAddress && scheduleModalAddress.description}}

          .flex-column.mt-4
            v-row(v-for="(day, index) in 7" :key="index")
              v-col(cols="12" sm="4")
                v-checkbox(
                  :input-value="(scheduleModalAddress.workingDays || []).includes(dayIndexToUTC(index))"
                  :label="$vuetify.lang.t(`$vuetify.days.${index}`)"
                  @change="toggleAddressWorkday(index)"
                  color="secondary"
                )
              v-col(cols="12" sm="4")
                v-text-field(
                  v-model="scheduleModalAddress['openTime_' + dayIndexToUTC(index)]"
                  :label="$vuetify.lang.t('$vuetify.pages.EstablishmentEditor.openTime')"
                  type="time"
                  :disabled="!(scheduleModalAddress.workingDays || []).includes(dayIndexToUTC(index))"
                  outlined
                  hide-details
                )
              v-col(cols="12" sm="4")
                v-text-field(
                  v-model="scheduleModalAddress['closeTime_' + dayIndexToUTC(index)]"
                  :label="$vuetify.lang.t('$vuetify.pages.EstablishmentEditor.closeTime')"
                  :disabled="!(scheduleModalAddress.workingDays || []).includes(dayIndexToUTC(index))"
                  type="time"
                  outlined
                  hide-details
                )
        v-card-actions.d-flex.justify-space-between
          v-btn(@click="scheduleModal = false; scheduleModalAddress = {}" text) {{$vuetify.lang.t('$vuetify.buttons.cancel')}}
          v-btn.white--text(@click="saveAddressSchedule()" color="black") {{$vuetify.lang.t('$vuetify.buttons.save')}}
</template>

<script>
import Vue from 'vue';
import request from '@/utils/request';

import { getFormData } from '@/utils/helpers';
import { mapActions, mapGetters, mapState } from 'vuex';
import { debounce } from 'debounce';

import validate from '@/mixins/validate';
import { validationMixin } from 'vuelidate';
import {numeric, required, requiredIf, minValue as min, maxValue as max, decimal} from 'vuelidate/lib/validators';
import {localizeField, multilingualFieldKeys, multilingualFields} from "@/helpers/locales";

const vImageUploader = () => import('@/components/image-uploader');

const SEARCH_DEBOUNCE_TIME = 500;

export default {
    name: 'EstablishmentEditor',
    components: {
        vImageUploader
    },
    mixins: [validate, validationMixin],
    data: () => ({
        establishment: {
            ...multilingualFields(['name', 'description']),
            enabled: false,
            cookTime: null,
            minimalPrice: null,
            addresses: [],
            typeIds: [],
            categoryId: null,
            brandId: null,
            managerPhoneNumber: null,
            image: null,
            imgUrl: null,
            closedOnAlarm: false,
            margin: 0,
            openTime: null,
            closeTime: null,
            commission: 0
        },

        autocompleteAddresses: [],
        addressSearch: '',
        debouncedSearch: null,
        addressFormatter: Vue.filter('extendAddress'),
        scheduleModal: false,
        scheduleModalAddress: {},

        isLoading: false,
        requestProcessing: false,
        editingLocale: 'uk',

        canSaveChanges: false,
    }),
    validations: {
        establishment: {
            cookTime: { required, numeric },
            minimalPrice: { required, numeric },
            categoryId: { required },
            brandId: { required },
            commission: {
              min: min(0),
              max: max(100),
              decimal,
              required
            },
            margin: {
              min: min(0),
              max: max(100),
              decimal,
              required
            },
            image: {
                required: requiredIf(function () {
                    return !this.isEdit;
                }),
            }
        }
    },
    computed: {
        ...mapGetters(['userBrandId']),
        ...mapState({
            brands: state => state.data.brands,
            categories: state => state.establishmentsCategories.allItems,
            types: state => state.establishmentsTypes.allItems
        }),
        isEdit() {
            return !!this.$route.params.id;
        },
        modelAsString() {
            return JSON.stringify(this.establishment);
        },
        isCustomValid() {
          return multilingualFieldKeys(['name']).some((field) => !!this.establishment[field]);
        }
    },
    setup() {
        return {
            localizeField,
            dayIndexToUTC: (day) => day === 6 ? 0 : day + 1
        };
    },
    watch: {
        modelAsString() {
            this.canSaveChanges = true;
        },
        addressSearch(newValue, oldValue) {
            if (newValue && !oldValue) {
                return;
            }
            this.debouncedSearch(newValue);
        }
    },
    async created() {
        this.debouncedSearch = debounce(this.searchAddresses, SEARCH_DEBOUNCE_TIME);

        const handlers = [this.getBrands, this.getCategories, this.getTypes];
        await Promise.all(handlers.map((fn) => fn()));

        if (this.userBrandId) {
            this.establishment.brandId = this.userBrandId;
        }

        if (this.isEdit) {
            this.isLoading = true;
            this.loadActiveEstablishment();
        }
    },
    methods: {
        ...mapActions({
            getBrands: 'data/getBrands',
            getCategories: 'establishmentsCategories/getAllItems',
            getTypes: 'establishmentsTypes/getAllItems',
        }),
        toggleAvailability({addressId, currentStatus}) {
          this.$store.dispatch('establishments/toggleAvailability', {
            addressId,
            establishmentId: this.$route.params.id,
            newStatus: currentStatus === 'active' ? 'hidden' : 'active'
          });
        },
        async searchAddresses(addressValue) {
            if (!addressValue || addressValue.length <= 3) {
                return;
            }
            const { data: foundAddresses } = await request({
                method: 'get',
                url: `/orders/complete/addresses`,
                params: {
                    q: addressValue,
                    outCity: false,
                    lang: this.$vuetify.lang.current
                }
            });
            this.autocompleteAddresses = foundAddresses;
        },
        onAddressSelect(address) {
            const formattedAddress = this.addressFormatter(address);
            const { addresses } = this.establishment;
            const addressIndex = addresses.findIndex(it => it.osmId === formattedAddress.osmId);

            formattedAddress.status = 'active';

            if (addressIndex !== -1) {
                addresses.splice(addressIndex, 1, formattedAddress);
            } else {
                addresses.push(formattedAddress);
            }
            this.autocompleteAddresses = [];
        },
        onRemoveAddressClick(address) {
            const addressIndex = this.establishment.addresses.findIndex(it => it.id === address.id);
            if (addressIndex === -1) {
                return;
            }
            this.establishment.addresses.splice(addressIndex, 1);
        },
        openScheduleModal(address) {
            this.scheduleModal = true;
            this.scheduleModalAddress = JSON.parse(JSON.stringify(address));
        },
        saveAddressSchedule() {
          const addressIndex = this.establishment.addresses.findIndex(it => it.id === this.scheduleModalAddress.id);
          if (addressIndex === -1) {
            return;
          }

          this.establishment.addresses[addressIndex] = JSON.parse(JSON.stringify(this.scheduleModalAddress));
          this.scheduleModal = false;
          this.scheduleModalAddress = {};
        },
        toggleAddressWorkday(index) {
          if ((this.scheduleModalAddress.workingDays || []).includes(this.dayIndexToUTC(index))) {
            this.scheduleModalAddress.workingDays = (this.scheduleModalAddress.workingDays || []).filter((day) => day !== this.dayIndexToUTC(index));
          } else {
            this.scheduleModalAddress.workingDays = (this.scheduleModalAddress.workingDays || []).concat(this.dayIndexToUTC(index));
          }
        },
        onChooseImage(file) {
            this.canSaveChanges = true;
            this.establishment.image = file;
        },

        loadActiveEstablishment() {
            const { id } = this.$route.params;
            const url = `/delivery/establishment/${id}`;

            return request({ method: 'get', url })
                .then(this.handleSuccess)
                .catch(this.handlerError)
                .finally(() => {
                    this.requestProcessing = false;
                });
        },
        async handleSuccess(res) {
            const keys = Object.keys(this.establishment);
            const dataFromResponse = Object.select(res.data, keys);
            const typeIds = (res.data.types || []).map('typeId');
            const addresses = (res.data.addresses || []).map((item) => ({...this.addressFormatter(item.place), ...item}));

            this.establishment = { ...dataFromResponse,
                brandId: +res.data.brandId,
                categoryId: +res.data.categoryId,
                typeIds,
                addresses,
                commission: (parseFloat(res.data.commission) * 100).toFixed(2),
                margin: (parseFloat(res.data.margin) * 100).toFixed(2)
            };

            this.$nextTick(() => {
                this.canSaveChanges = true;
                this.isLoading = false;
            });
        },
        handlerError(error) {
            const message = error.response && error.response.data && error.response.data.error;

            if (message) {
                this.$store.dispatch('alert/addMessage', {
                    message: `brand.${message}`,
                    time: 2000
                });
            }

            if (error.response.status === 412) {
                this.$router.push('/establishments');
            }
        },
        async saveEstablishment() {
            if (this.$v.establishment.$invalid) {
                this.$v.establishment.$touch();
                this.$nextTick(() => this.focusFirstErrorInput());
                return;
            }

            if (this.establishment.image && this.establishment.image.size > 5*1000*1000) {
              return this.$store.dispatch('alert/addMessage', {
                message: `imageIsTooLarge`,
                time: 2000
              });
            }

            if (!this.establishment.addresses.length) {
                return this.$store.dispatch('alert/addMessage', {
                    message: 'Список адресов должен быть заполнен',
                    locale: false
                });
            }

            if (this.requestProcessing) return;

            this.requestProcessing = true;

            const { id } = this.$route.params;
            const method = id ? 'put' : 'post';
            const url = id ? `/delivery/establishment/${id}` : '/delivery/establishment';

            const ignoredKeys = ['createdAt', 'updatedAt', 'imgUrl', 'place'];
            const preparedData = Object.reject(this.establishment, ignoredKeys);

            preparedData.commission = parseFloat(preparedData.commission) / 100;
            preparedData.margin = parseFloat(preparedData.margin) / 100;

            const data = getFormData(preparedData);
            data.set('addresses', JSON.stringify(this.establishment.addresses));
            data.set('typeIds', this.establishment.typeIds.join(','));

            return request({ method, url, data })
                .then(res => {
                    this.handleSuccess(res);
                    this.$store.dispatch('alert/addMessage', {
                        type: 'success',
                        message: 'success'
                    });

                    if (res.status === 201) {
                        this.$router.push(`/establishments/editor/${res.data.id}`);
                    }

                    this.requestProcessing = false;
                    this.$v.establishment.$reset();
                })
                .catch(this.handlerError)
                .finally(() => {
                    this.requestProcessing = false;
                });
        }
    }
};
</script>

<style lang="scss" scoped>
.editor-title {
    display: flex;
    justify-content: space-between;

    .establishment-status {
        max-width: 150px;
        width: 100%;
    }

    .establishment-alarm-activity {
        min-width: 230px;
        width: 100%;
    }
}

.addresses-wrap {
    padding: 10px;
    background: #fff;

    .address {
        cursor: default;
        font-size: 15px;
        margin-bottom: 10px;
        display: flex;
        align-items: center;
        justify-content: space-between;

        &:last-child {
            margin-bottom: unset;
        }

        &__description {
            width: 100%;
        }

        &__icon {
            margin-right: 5px;
        }

        &__actions {
            margin-left: 5px;
        }
    }
}

.address-input {
    ::placeholder {
        font-size: 14px;
        color: grey !important;
    }

    ::v-deep .v-input__append-outer {
        position: absolute;
        left: 100%;
        margin-top: unset;
        align-self: center;
    }
}

.remove-address-btn {
    margin-left: auto;
}
</style>
